import { createApp } from '@backstage/frontend-defaults';
import {
  NavContentBlueprint,
  SignInPageBlueprint,
} from '@backstage/plugin-app-react';
import type { NavContentComponentProps } from '@backstage/plugin-app-react';
import {
  Sidebar,
  SidebarDivider,
  SidebarItem,
  SignInPage,
} from '@backstage/core-components';
import { googleAuthApiRef } from '@backstage/core-plugin-api';
import { createFrontendModule } from '@backstage/frontend-plugin-api';

import { apiModule } from './apiModule';
import { apiDocsModule } from './apiDocsModule';
import { catalogModule } from './catalogModule';
import { homeModule } from './homeModule';
import { techdocsModule } from './techdocsModule';

// ── Custom sidebar ────────────────────────────────────────────────────────────
// Place Home first, then all other auto-discovered items top-aligned.
// No SidebarSpace/flex-grow between items so everything sticks to the top.

function ToolSenseNavContent({ navItems }: NavContentComponentProps) {
  const renderer = navItems.withComponent(({ href, title, icon }) => (
    // SidebarItem needs IconComponent; wrap the IconElement from NavContentNavItem.
    <SidebarItem to={href} text={title} icon={(() => icon) as any} />
  ));

  // Hide the standalone Catalog Graph page from the sidebar; the route stays
  // reachable via entity-level "View graph" links. There is no separate
  // nav-item extension in this frontend-system version — take()-and-discard
  // by the PAGE extension ID is the only way to drop an item from the nav.
  renderer.take('page:catalog-graph');

  return (
    // Must include <Sidebar> wrapper — DefaultNavContent does the same.
    // take() uses the PAGE extension ID (e.g. 'page:home'), not 'nav-item:home'.
    <Sidebar>
      {renderer.take('page:home')}
      <SidebarDivider />
      {renderer.rest()}
    </Sidebar>
  );
}

const appNavModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    NavContentBlueprint.make({
      params: {
        component: ToolSenseNavContent,
      },
    }),
  ],
});

// ── Sign-in page ──────────────────────────────────────────────────────────────

const appModuleSignInPage = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPageBlueprint.make({
      params: {
        loader: async () => props =>
          (
            <SignInPage
              {...props}
              auto
              provider={{
                id: 'google-auth-provider',
                title: 'Google',
                message: 'Sign in using Google',
                apiRef: googleAuthApiRef,
              }}
            />
          ),
      },
    }),
  ],
});

const app = createApp({
  features: [
    appModuleSignInPage,
    appNavModule,
    apiModule,
    apiDocsModule,
    catalogModule,
    homeModule,
    techdocsModule,
  ],
});

export default app.createRoot();
